import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCategoryClick = (url: string) => {
    setDropdownOpen(false);
    setMobileMenuOpen(false);
    setMobileCatOpen(false);
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50 shadow-[0_1px_3px_rgba(0,0,0,0.08)] border-b border-background-200/80'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-6 md:px-10">
        <div className="flex items-center justify-between h-16 md:h-18">
          {/* Left nav links - desktop */}
          <div className="hidden md:flex items-center gap-6">
            <Link
              to="/"
              className={`text-sm font-semibold whitespace-nowrap transition-colors cursor-pointer ${
                scrolled ? 'text-foreground-900 hover:text-primary-600' : 'text-white hover:text-accent-300'
              }`}
            >
              Início
            </Link>

            {/* Categories dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className={`flex items-center gap-1 text-sm font-semibold whitespace-nowrap transition-colors cursor-pointer ${
                  scrolled ? 'text-foreground-900 hover:text-primary-600' : 'text-white hover:text-accent-300'
                }`}
              >
                Categorias
                <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-72 bg-background-50 rounded-xl shadow-lg border border-background-200/70 py-3 max-h-96 overflow-y-auto">
                  <p className="px-4 pb-2 text-xs font-semibold text-foreground-400 uppercase tracking-wider">14 Categorias de Produtos</p>
                  {categories.map((cat) => (
                    <button
                      key={cat.seq}
                      onClick={() => handleCategoryClick(cat.url)}
                      className="w-full text-left px-4 py-2.5 text-sm text-foreground-700 hover:bg-accent-100 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-between group"
                    >
                      <span className="font-medium">{cat.namePt}</span>
                      <span className="text-xs text-foreground-400 group-hover:text-accent-600 ml-2">{cat.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/guia-cssbuy-sheet-2026"
              className={`text-sm font-semibold whitespace-nowrap transition-colors cursor-pointer ${
                scrolled ? 'text-foreground-900 hover:text-primary-600' : 'text-white hover:text-accent-300'
              }`}
            >
              Guia de Compras
            </Link>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className={`text-sm font-semibold whitespace-nowrap transition-colors cursor-pointer ${
                scrolled ? 'text-foreground-900 hover:text-primary-600' : 'text-white hover:text-accent-300'
              }`}
            >
              Artigos SEO
            </a>
          </div>

          {/* Mobile hamburger */}
          <div className="md:hidden flex items-center">
            <button
              onClick={() => {
                setMobileMenuOpen(!mobileMenuOpen);
                setDropdownOpen(false);
              }}
              className={`w-10 h-10 flex items-center justify-center rounded-lg transition-colors ${
                scrolled ? 'text-foreground-900 hover:bg-background-200' : 'text-white hover:bg-white/10'
              }`}
            >
              <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>

          {/* Center logo */}
          <div className="flex items-center gap-2">
            <Link to="/" className="flex items-center gap-2 cursor-pointer">
              <div className={`w-9 h-9 rounded-full flex items-center justify-center transition-colors ${
                scrolled ? 'bg-primary-500' : 'bg-white/20'
              }`}>
                <i className={`ri-file-list-3-line text-base ${scrolled ? 'text-background-50' : 'text-white'}`}></i>
              </div>
              <span className={`text-lg font-bold whitespace-nowrap transition-colors ${
                scrolled ? 'text-foreground-950' : 'text-white'
              }`}>
                CSSBuy Sheet
              </span>
            </Link>
          </div>

          {/* Right side - desktop */}
          <div className="hidden md:flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center gap-2 px-5 py-2 bg-primary-500 text-background-50 text-sm font-semibold rounded-full hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              cssbuy sheet
              <i className="ri-arrow-right-up-line text-sm"></i>
            </a>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className={`flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-full transition-colors cursor-pointer whitespace-nowrap border ${
                scrolled
                  ? 'bg-foreground-950 text-background-50 border-foreground-950 hover:bg-foreground-800'
                  : 'bg-white text-primary-700 border-white/60 hover:bg-foreground-950 hover:text-white'
              }`}
            >
              buy it now
              <i className="ri-arrow-right-up-line text-sm"></i>
            </a>
          </div>

          {/* Mobile right buttons */}
          <div className="md:hidden flex items-center gap-2">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className={`flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-full whitespace-nowrap transition-colors ${
                scrolled
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-white/20 text-white'
              }`}
            >
              buy it now
              <i className="ri-arrow-right-up-line text-xs"></i>
            </a>
          </div>
        </div>

        {/* Mobile menu panel */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-background-50 rounded-xl shadow-lg border border-background-200/70 mt-2 overflow-hidden">
            {/* Main links */}
            <div className="py-1">
              <Link
                to="/"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 px-5 py-3.5 text-sm font-semibold text-foreground-900 hover:bg-background-100 transition-colors"
              >
                <i className="ri-home-4-line text-lg text-primary-500"></i>
                Início
              </Link>

              <button
                onClick={() => setMobileCatOpen(!mobileCatOpen)}
                className="w-full flex items-center justify-between px-5 py-3.5 text-sm font-semibold text-foreground-900 hover:bg-background-100 transition-colors"
              >
                <span className="flex items-center gap-3">
                  <i className="ri-grid-line text-lg text-accent-500"></i>
                  Categorias
                </span>
                <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${mobileCatOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {/* Mobile category submenu */}
              {mobileCatOpen && (
                <div className="bg-background-100 border-t border-background-200/70">
                  <div className="px-4 py-3">
                    <div className="flex flex-wrap gap-1.5">
                      {categories.map((cat) => (
                        <button
                          key={cat.seq}
                          onClick={() => handleCategoryClick(cat.url)}
                          className="flex items-center gap-2 px-3 py-2 bg-background-50 rounded-lg border border-background-200/70 text-xs font-medium text-foreground-700 hover:bg-accent-100 hover:border-accent-300 transition-colors cursor-pointer whitespace-nowrap"
                        >
                          {cat.namePt}
                          <i className="ri-external-link-line text-foreground-400"></i>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              <Link
                to="/guia-cssbuy-sheet-2026"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 px-5 py-3.5 text-sm font-semibold text-foreground-900 hover:bg-background-100 transition-colors"
              >
                <i className="ri-book-open-line text-lg text-secondary-600"></i>
                Guia de Compras
              </Link>

              <Link
                to="/cssbuy-sheet-portugal-brasil"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 px-5 py-3.5 text-sm font-semibold text-foreground-900 hover:bg-background-100 transition-colors"
              >
                <i className="ri-earth-line text-lg text-accent-500"></i>
                Portugal &amp; Brasil
              </Link>

              <Link
                to="/melhor-cssbuy-spreadsheet-2026"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 px-5 py-3.5 text-sm font-semibold text-foreground-900 hover:bg-background-100 transition-colors"
              >
                <i className="ri-star-line text-lg text-primary-500"></i>
                Melhor Planilha 2026
              </Link>

              <Link
                to="/cssbuy-spreadsheet-seguro"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 px-5 py-3.5 text-sm font-semibold text-foreground-900 hover:bg-background-100 transition-colors"
              >
                <i className="ri-shield-check-line text-lg text-accent-500"></i>
                Compras Seguras
              </Link>

              <Link
                to="/como-comprar-cssbuy-sheet"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 px-5 py-3.5 text-sm font-semibold text-foreground-900 hover:bg-background-100 transition-colors"
              >
                <i className="ri-shopping-cart-line text-lg text-secondary-600"></i>
                Como Comprar
              </Link>
            </div>

            <div className="border-t border-background-200/70 p-5">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center justify-center gap-2 w-full px-5 py-3 bg-primary-500 text-background-50 text-sm font-semibold rounded-full hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                cssbuy sheet
                <i className="ri-arrow-right-up-line text-base"></i>
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}